import { graphQlUriAtom } from "@/admin/graphql/graphQlUriAtom.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { HeroBlock } from "@/components/HeroBlock.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { useConfig } from "@/config.ts";
import { ApiDocLink } from "@/modules/access/token/ApiDocLink.tsx";
import { getApiUrl } from "@/modules/api/createApiClient.ts";
import R from "@/modules/billing/referral/ReferralProgram.module.css";
import { useAtomValue } from "jotai/react";
import { Link } from "react-aria-components";

function getGrafanaUri(url: string) {
    return url.replace("graphql", "grafana");
}

function getSupportUri() {
    const { hostname } = window.location;
    return `https://warrenio.atlassian.net/servicedesk/customer/portal/2/group/2/create/12?customfield_10533=https://${hostname}`;
}

function LinkIcon() {
    return <MaskIcon className="jp-icon-text-editor color-primary size-0.75rem" />;
}

export function HelpView() {
    const { apiGrafanaUrl } = useConfig();
    const graphqlUri = useAtomValue(graphQlUriAtom);
    const grafanaUri = apiGrafanaUrl ? apiGrafanaUrl : getGrafanaUri(graphqlUri);

    return (
        <HeroBlock title="Help" icon="jp-help-icon" className="flex flex-col gap-3 font-size-default">
            <p>
                This page provides essential guidance and support for administrators managing the platform. Whether
                you’re setting up user roles, configuring system settings, or troubleshooting common issues, you’ll find
                instructions and best practices to streamline your workflow. Explore the topics below to get the most
                out of your admin tools and ensure a smooth experience for your team. If you need further assistance,
                our support team is always here to help.
            </p>

            <div className={R.Share}>
                <div>
                    <div className="pb-1">API URL</div>
                    <div className={R.dummyField}>
                        <ClipBoardTooltip>{getApiUrl("")}</ClipBoardTooltip>
                    </div>
                </div>

                <div>
                    <div className="pb-1">GraphQL URL</div>
                    <div className={R.dummyField}>
                        <ClipBoardTooltip>{graphqlUri}</ClipBoardTooltip>
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-1">
                <div className="text-uppercase">URLs to know</div>

                <ApiDocLink>
                    <LinkIcon /> API Documentation
                </ApiDocLink>

                <Link href="https://documenter.getpostman.com/view/2599901/RznLEagg" target="_blank">
                    <LinkIcon /> Admin API documentation
                </Link>

                <Link
                    href="https://warrenio.atlassian.net/wiki/spaces/WARP/pages/53215252/Feature+docs+and+admin+guides"
                    target="_blank"
                >
                    <LinkIcon /> Feature docs and admin guides
                </Link>

                <Link
                    href="https://warrenio.atlassian.net/wiki/spaces/WARP/pages/42237961/Release+Notes"
                    target="_blank"
                >
                    <LinkIcon /> Release Notes
                </Link>

                <Link href={getSupportUri()} target="_blank">
                    <LinkIcon /> Support tickets
                </Link>

                <Link href={`https://studio.apollographql.com/sandbox?endpoint=${graphqlUri}`} target="_blank">
                    <LinkIcon /> GraphQL Apollo Studio
                </Link>
                <Link href={grafanaUri} target="_blank">
                    <LinkIcon /> Grafana
                </Link>
            </div>
        </HeroBlock>
    );
}
